'use client';
import { StudioAction } from '@/components/ui/StudioAction';
import { CreationGlassBackdrop } from '@/components/visual-editor/CreationGlassBackdrop';
import creationChrome from '@/components/visual-editor/CreationChrome.module.css';
import { SelectField, SelectItem } from '@/components/ui/SelectField';

import { prepareBrowserImageFile, isImageFile, IMAGE_FILE_INPUT_ACCEPT } from '@/lib/api/materials';

import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle2, Paperclip, X } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useBodyScrollLock } from '@/lib/hooks/useBodyScrollLock';
import { useDialogFocus } from '@/lib/hooks/useDialogFocus';
import { getAuthSessionVersion, useAuthStore } from '@/stores/authStore';
import { SystemLoadingIcon } from '@/components/ui/SystemLoading';
import { supportApi, SUPPORT_CATEGORIES, SUPPORT_SURFACES, SUPPORT_SCREENSHOT_LIMIT, SUPPORT_SCREENSHOT_BYTES, type SupportPayload } from '@/lib/api/support';
import styles from './SupportDialog.module.css';

interface Screenshot { id: string; name: string; content: string; preview: string }
function readScreenshot(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('screenshot_read_failed'));
    reader.onload = () => resolve(String(reader.result));
    reader.readAsDataURL(file);
  });
}

export function SupportDialog({ onClose }: { onClose: () => void }) {
  const t = useTranslations('supportForm');
  const id = useId();
  const panel = useRef<HTMLDivElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const busy = useRef(false);
  const mounted = useRef(true);
  const [category, setCategory] = useState<SupportPayload['category'] | ''>('');
  const [surface, setSurface] = useState<SupportPayload['surface'] | ''>('');
  const [description, setDescription] = useState('');
  const [screenshots, setScreenshots] = useState<Screenshot[]>([]);
  const [loading, setLoading] = useState(false);
  const [reading, setReading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [attempted, setAttempted] = useState(false);
  const userId = useAuthStore((state) => state.user?.id);
  const originalUser = useRef(userId);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const requestClose = () => { if (!busy.current) closeRef.current(); };
  useBodyScrollLock(true);
  useDialogFocus(panel, true, () => { if (!busy.current) closeRef.current(); });

  useEffect(() => {
    if (userId !== originalUser.current) closeRef.current();
  }, [userId]);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  useEffect(() => {
    if (sent) panel.current?.querySelector<HTMLButtonElement>('button')?.focus();
  }, [sent]);

  async function addScreenshots(files: File[]) {
    if (!files.length || busy.current) return;
    if (screenshots.length + files.length > SUPPORT_SCREENSHOT_LIMIT || files.some(file =>
      !isImageFile(file) || file.size > SUPPORT_SCREENSHOT_BYTES || !file.size)) {
      setError(t('fileError')); return;
    }
    const session = getAuthSessionVersion();
    busy.current = true; setReading(true); setError('');
    try {
      const added = await Promise.all(files.map(async file => {
        const prepared = await prepareBrowserImageFile(file);
        if (prepared.size > SUPPORT_SCREENSHOT_BYTES) throw new Error('screenshot_size');
        const preview = await readScreenshot(prepared);
        return { id: crypto.randomUUID(), name: file.name, preview, content: preview.slice(preview.indexOf(',') + 1) };
      }));
      if (mounted.current && getAuthSessionVersion() === session) setScreenshots(current => [...current, ...added]);
    } catch { if (mounted.current && getAuthSessionVersion() === session) setError(t('fileError')); }
    finally { busy.current = false; if (mounted.current) setReading(false); }
  }
  async function submit(event: FormEvent) {
    event.preventDefault();
    if (busy.current || sent) return;
    setAttempted(true);
    if (!category || !surface || !description.trim()) {
      setError(t('required'));
      document.getElementById(`${id}-${!category ? 'category' : !surface ? 'surface' : 'description'}`)?.focus();
      return;
    }
    busy.current = true; setLoading(true); setError('');
    const session = getAuthSessionVersion();
    try {
      const response = await supportApi.submit({ category, surface, description: description.trim(), screenshots: screenshots.map(({ content }) => ({ content })) });
      if (!mounted.current || getAuthSessionVersion() !== session) return;
      if (!response.success || response.data?.status !== 'sent') {
        setError(t(response.error?.httpStatus === 429 ? 'rateLimited' : 'submitError')); return;
      }
      setSent(true);
    } catch { if (mounted.current && getAuthSessionVersion() === session) setError(t('submitError')); }
    finally { busy.current = false; if (mounted.current) setLoading(false); }
  }

  return createPortal(
    <div className={styles.overlay} onMouseDown={event => { if (event.target === event.currentTarget && !busy.current) onClose(); }}>
      <div ref={panel} role="dialog" aria-modal="true" aria-labelledby={`${id}-title`} aria-describedby={`${id}-intro`} className={`${creationChrome.chrome} ${styles.panel}`} tabIndex={-1}>
        <CreationGlassBackdrop />
        <header className={styles.header}>
          <h2 id={`${id}-title`}>{t('title')}</h2>
          <button type="button" aria-label={t('close')} onClick={requestClose} disabled={loading || reading} className={styles.close}><X size={24} aria-hidden /></button>
          <p id={`${id}-intro`}>{t('intro')}</p>
        </header>
        {sent ? <div className={styles.success} role="status">
          <CheckCircle2 size={44} color="#4ABDE9" aria-hidden />
          <h3>{t('successTitle')}</h3><p>{t('successBody')}</p>
          <StudioAction type="button" className={styles.primary} onClick={onClose}>{t('done')}</StudioAction>
        </div> : <form onSubmit={submit} noValidate aria-busy={loading || reading}>
          <div className={styles.fields}>
            <div className={styles.field} id={`${id}-category-field`} role="group" aria-labelledby={`${id}-category-label`} data-invalid={attempted && !category}><label id={`${id}-category-label`} htmlFor={`${id}-category`}>{t('category')}</label>
              <SelectField className={styles.select} id={`${id}-category`} aria-describedby={attempted && !category ? `${id}-error` : undefined} value={category} disabled={loading} onValueChange={nextValue => setCategory(nextValue as SupportPayload['category'])}>
                <SelectItem value="" disabled>{t('categoryPlaceholder')}</SelectItem>{SUPPORT_CATEGORIES.map(value => <SelectItem key={value} value={value}>{t(`categories.${value}`)}</SelectItem>)}
              </SelectField>
            </div>
            <div className={styles.field} id={`${id}-surface-field`} role="group" aria-labelledby={`${id}-surface-label`} data-invalid={attempted && !surface}><label id={`${id}-surface-label`} htmlFor={`${id}-surface`}>{t('surface')}</label>
              <SelectField className={styles.select} id={`${id}-surface`} aria-describedby={attempted && !surface ? `${id}-error` : undefined} value={surface} disabled={loading} onValueChange={nextValue => setSurface(nextValue as SupportPayload['surface'])}>
                <SelectItem value="" disabled>{t('surfacePlaceholder')}</SelectItem>{SUPPORT_SURFACES.map(value => <SelectItem key={value} value={value}>{t(`surfaces.${value}`)}</SelectItem>)}
              </SelectField>
            </div>
            <div className={styles.field}><label htmlFor={`${id}-description`}>{t('description')}</label>
              <textarea id={`${id}-description`} required aria-invalid={attempted && !description.trim()} maxLength={1000} value={description} disabled={loading} placeholder={t('descriptionPlaceholder')} onChange={event => setDescription(event.target.value)} aria-describedby={`${id}-count${attempted && !description.trim() ? ` ${id}-error` : ''}`} />
              <p id={`${id}-count`} className={styles.count}>{description.length}/1000</p>
            </div>
            <div className={styles.attachmentRow}>
              <input ref={fileInput} type="file" hidden multiple accept={IMAGE_FILE_INPUT_ACCEPT} onChange={event => { const files = Array.from(event.target.files || []); event.target.value = ''; void addScreenshots(files); }} />
              <StudioAction variant="secondary" type="button" className={styles.secondary} aria-busy={reading} disabled={loading || reading || screenshots.length >= SUPPORT_SCREENSHOT_LIMIT} onClick={() => fileInput.current?.click()}>
                {reading ? <SystemLoadingIcon className="h-5 w-5" /> : <Paperclip size={20} aria-hidden />}{t('addScreenshot')}
              </StudioAction><span>{t('optional')}</span>
            </div>
            <p className={styles.hint}>{t('fileHint')}</p>
            {screenshots.length > 0 && <ul className={styles.previews}>{screenshots.map(item => <li key={item.id}>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src={item.preview} alt={item.name} /><span title={item.name}>{item.name}</span>
              <button type="button" disabled={loading || reading} aria-label={t('removeScreenshot', { name: item.name })} onClick={() => setScreenshots(current => current.filter(image => image.id !== item.id))}><X size={16} aria-hidden /></button>
            </li>)}</ul>}
            {error && <p id={`${id}-error`} className={styles.error} role="alert">{error}</p>}
          </div>
          <footer className={styles.footer}>
            <StudioAction variant="secondary" type="button" className={styles.secondary} disabled={loading || reading} onClick={requestClose}>{t('cancel')}</StudioAction>
            <StudioAction type="submit" className={styles.primary} disabled={loading || reading} aria-busy={loading}>{loading && <SystemLoadingIcon className="h-4 w-4" />}{t(loading ? 'submitting' : 'submit')}</StudioAction>
          </footer>
        </form>}
      </div>
    </div>, document.body,
  );
}
